import React, { type ReactNode, useContext } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import {
  Dropdown,
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { Label } from "simplydo/react/ui/label";

import api from "api";
import util from "utils/utils";

import type { ILaneData } from "./Board";
import { ProjectBoardContext } from "./ProjectBoardContext";

export type ILaneSettings = {
  lane: ILaneData;
  editLaneName: () => void;
  editLaneDescription: () => void;
  updateLane: (
    data: Partial<OpenAPI.Schemas["ProjectLane"]>,
    onComplete?: () => void,
    onError?: () => void,
  ) => void;
  trigger: ReactNode;
  reorderLaneIdeas: (sort: string, sortDirection: string) => void;
};

export const DropdownLaneSettings = ({
  lane,
  editLaneName,
  editLaneDescription,
  updateLane,
  trigger,
  reorderLaneIdeas,
}: ILaneSettings) => {
  const { forId, lanes, checkUpdateProjectLane } =
    useContext(ProjectBoardContext);

  const { cards } = lane;

  const { t } = useTranslation();

  const challengeQuery = useQuery(initUseQuery(api.challenges.get, [forId]));
  const challenge = challengeQuery.data;
  const ideaStatuses = challenge?.ownerOrganisation?.ideaStatuses ?? [];

  const currentLaneId = lane.id || "default";
  const isDefaultLane = currentLaneId === "default";

  const LaneSortOptions: { text: string; value: string }[] = [
    { value: "projectUpdated", text: "Last update" },
    { value: "projectDeadline", text: "Project deadline" },
    {
      value: "ideaCreated",
      text: `${t("common:capitalise", { key: "generic.idea" })} creation`,
    },
    { value: "alphabetical", text: "Card name" },
    { value: "assessmentScore", text: "Average assessment score" },
  ];

  const moveLane = (direction: "left" | "right") => {
    const offset = direction === "left" ? -1 : 1;
    if (direction === "left" && lane.order < 1) return;
    if (direction === "right" && lane.order >= lanes.length - 1) return;
    api.boards.updateProjectLane(
      forId,
      currentLaneId,
      {
        order: lane.order + offset,
      } as OpenAPI.PUT<"/boards/{for_id}/lanes/{lane_id}">["requestBody"],
      () => {
        api.boards.getProjectLanes(
          forId,
          () => {
            api.invalidateQueries(api.boards.getProjectLanes, [forId]);
          },
          () => {},
        );
      },
      (err) => toast.error(err.message),
    );
  };

  const deleteLane = () => {
    util
      .confirm(
        "Really delete this lane?",
        `All ${t("generic.ideas")} on this lane will be moved back to the default lane.`,
      )
      .then(() => {
        api.boards.deleteProjectLane(
          forId,
          currentLaneId,
          () => {
            api.invalidateQueries(api.boards.getProjectLanes, [forId]);
            api.invalidateQueries(api.challenges.getIdeas, [forId]);
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  };

  return (
    <Dropdown direction="left" trigger={trigger}>
      <DropdownMenu>
        <DropdownHeader>Lane Settings</DropdownHeader>
        <DropdownDivider />
        {lanes.length > 1 && !isDefaultLane ? (
          <Dropdown trigger={<DropdownItem>Move lane</DropdownItem>}>
            <DropdownMenu>
              {lane.order > 0 ? (
                <DropdownItem onClick={() => moveLane("left")}>
                  Move left
                </DropdownItem>
              ) : null}
              {lane.order < lanes.length - 1 ? (
                <DropdownItem onClick={() => moveLane("right")}>
                  Move right
                </DropdownItem>
              ) : null}
            </DropdownMenu>
          </Dropdown>
        ) : null}
        {cards?.length ? (
          <Dropdown
            trigger={
              <DropdownItem>
                {`Reorder ${t("generic.ideas")} by...`}
              </DropdownItem>
            }
          >
            <DropdownMenu>
              {LaneSortOptions.map((l, i) => (
                <React.Fragment key={l.value}>
                  {i !== 0 ? <DropdownDivider /> : null}
                  <DropdownItem
                    onClick={() => reorderLaneIdeas(l.value, "desc")}
                  >
                    {`${l.text} (descending)`}
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => reorderLaneIdeas(l.value, "asc")}
                  >
                    {`${l.text} (ascending)`}
                  </DropdownItem>
                </React.Fragment>
              ))}
            </DropdownMenu>
          </Dropdown>
        ) : null}

        {ideaStatuses.length > 0 ? (
          <Dropdown
            trigger={<DropdownItem>Assign status to lane</DropdownItem>}
            value={lane.status ?? "none"}
          >
            <DropdownMenu>
              <DropdownItem
                value={"none"}
                onClick={() =>
                  updateLane({ status: null } as unknown as Partial<
                    OpenAPI.Schemas["ProjectLane"]
                  >)
                }
              >
                Unassigned
              </DropdownItem>
              <DropdownDivider />
              {ideaStatuses.map((status) => (
                <DropdownItem
                  value={status._id}
                  key={status._id}
                  onClick={() =>
                    updateLane({ status: status._id } as Partial<
                      OpenAPI.Schemas["ProjectLane"]
                    >)
                  }
                >
                  <Label size="small" color={status.color}>
                    {status.name}
                  </Label>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        ) : null}

        {cards?.length && lanes?.length > 0 ? (
          <Dropdown
            trigger={
              <DropdownItem>
                {`Move all ${t("generic.ideas")} to other lane`}
              </DropdownItem>
            }
          >
            <DropdownMenu>
              {lanes
                .filter((l) => l._id !== currentLaneId)
                .map((l) => (
                  <DropdownItem
                    key={l._id}
                    onClick={() =>
                      checkUpdateProjectLane(
                        cards.map((card) => card.idea._id),
                        l._id,
                        null,
                      )
                    }
                  >
                    {l.name}
                  </DropdownItem>
                ))}
            </DropdownMenu>
          </Dropdown>
        ) : null}
        {!isDefaultLane ? <DropdownDivider /> : null}
        <DropdownItem onClick={() => editLaneName()}>
          Edit lane name
        </DropdownItem>
        <DropdownItem onClick={() => editLaneDescription()}>
          Edit lane description
        </DropdownItem>
        {!isDefaultLane ? (
          <DropdownItem
            style={{ color: "#db2828" }}
            onClick={() => deleteLane()}
          >
            Delete lane
          </DropdownItem>
        ) : null}
      </DropdownMenu>
    </Dropdown>
  );
};
